import React, { useEffect, useState, useCallback } from "react";
import DataTableBase from "../../Components/Common/DataTableBase/DataTableBase";
import { t } from "i18next";
import { useStaffStore } from "../../stores/Staffs";
import { useConfirmDialogStore } from "../../stores/Modal/ConfirmDialogStore";
import EditUser from "./components/EditUser";
import AddUser from "./components/AddUser";
import EditPermissions from "./components/EditPermissions";
import moment from "moment";
import SearchBar from "../../Components/Common/SearchBar";
import { Button } from "reactstrap";
import Skeleton from "../../Components/Common/Skeleton";

export const StaffsPage = () => {
  const title = "Users";
  // API Call
  const {
    getAllUsers,
    isLoading,
    isError,
    users,
    deleteUser,
    searchUsers,
    pagination,
  } = useStaffStore((state) => state);
  const { showConfirm } = useConfirmDialogStore((state) => state);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showEditingModal, setshowEditingModal] = useState(false);
  const [showAddingModal, setshowAddingModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Pagination State
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    getAllUsers({
      page: 1,
      perPage: 10,
    });
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchText) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (searchText && searchText.trim() !== "") {
            setIsSearching(true);
            searchUsers(searchText);
          } else {
            setIsSearching(false);
            getAllUsers({
              page: currentPage,
              perPage: 10,
            });
          }
        }, 300); // 300ms delay
      };
    })(),
    [currentPage, searchUsers, getAllUsers]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    // Only fetch when not searching
    if (!isSearching && (!searchTerm || searchTerm.trim() === "")) {
      getAllUsers({
        page: currentPage,
        perPage: 10,
      });
    }
  }, [currentPage, isSearching, searchTerm, getAllUsers]);

  useEffect(() => {
    setTotalRows(pagination?.totalItems || 0);
  }, [pagination]);

  const displayRole = (role) => {
    let formattedRole = role?.toUpperCase();
    if (formattedRole === "SUPER_ADMIN" || formattedRole === "ROOT") {
      return "ROOT";
    } else if (formattedRole === "ADMIN") {
      return "ADMIN";
    } else if (formattedRole === "NORMAL_USER" || formattedRole === "USER") {
      return "USER";
    }
    return "";
  };

  // Calculate the current data slice

  const columns = [
    {
      name: t("ID"),
      width: "100px",
      selector: (row) => row.user_id,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Created"),
      width: "120px",
      selector: (row) => moment(row?.created_at).format("YY/MM/DD hh:mm"),
      sortable: true,
      wrap: true,
    },
    {
      name: t("Name"),
      selector: (row) => row.name,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Email"),
      width: "250px",
      selector: (row) => row.email,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Role"),
      width: "90px",
      selector: (row) => row.role,
      sortable: true,
      wrap: true,
      cell: (row) => <div>{displayRole(row.role)}</div>,
    },
    {
      name: t("Call Teaboy"),
      selector: (row) => row.canCallTeaboy,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div
          style={{ fontSize: "14px" }}
          className={`badge cursor-normal bg-soft-${row.canCallTeaboy ? "success" : "danger"
            } text-${row.canCallTeaboy ? "success" : "danger"}`}
        >
          {row.canCallTeaboy ? "Yes" : "No"}
        </div>
      ),
    },
    {
      name: t("Registered"),
      selector: (row) => row.signedUp,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div
          style={{ fontSize: "14px" }}
          className={`badge cursor-normal bg-soft-${row.signedUp ? "success" : "danger"
            } text-${row.signedUp ? "success" : "danger"}`}
        >
          {row.signedUp ? "Yes" : "No"}
        </div>
      ),
    },
    {
      name: t("Image"),
      width: "80px",
      selector: (row) => row.image_url,
      sortable: false,
      cell: (row) =>
        row.image_url ? (
          <img src={row.image_url} alt={row.name} width={50} />
        ) : (
          "N/A"
        ),
      wrap: true,
    },
    {
      name: t("Phone"),
      selector: (row) => row.phone,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Verified"),
      selector: (row) => row.isVerified,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div
          style={{ fontSize: "14px" }}
          className={`badge cursor-normal bg-soft-${row.isVerified ? "success" : "danger"
            } text-${row.isVerified ? "success" : "danger"}`}
        >
          {row.isVerified ? "Yes" : "No"}
        </div>
      ),
    },
    {
      name: t("App Version"),
      selector: (row) => row.appVersion,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Last Login"),
      selector: (row) => row.last_login,
      sortable: true,
      wrap: true,
      width: "120px",

      cell: (row) => (
        <p>
          {row.last_login
            ? moment(row.last_login).format("DD/MM/YYYY hh:mm")
            : "N/A"}
        </p>
      ),
    },
  ];

  const toggleEditUserModal = (row) => {
    setSelectedRow(row);
    setshowEditingModal(!showEditingModal);
  };

  const toggleCreateModal = () => {
    setshowAddingModal(!showAddingModal);
  };

  const togglePermissionsModal = (row) => {
    setSelectedRow(row);
    setShowPermissionsModal(!showPermissionsModal);
  };

  const deleteUserFun = async (id) => {
    try {
      await deleteUser(id);
    } catch (e) {
      console.log(e);
    }
  };
  const onChangePage = (page) => {
    setCurrentPage(page);
  };

  const searchHandler = (searchText) => {
    setSearchTerm(searchText);
    if (searchText && searchText.trim() !== "") {
      setCurrentPage(1); // Reset to first page on search
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
  };

  return (
    <>
      <DataTableBase
        tableTitle={title}
        data={users} // Pass only the current slice of data
        columns={columns}
        loading={isLoading}
        paginationTotalRows={isSearching ? users.length : pagination?.totalItems}
        onChangePage={onChangePage} // Handle page change
        showSubHeader={false}
        showActionButtons={false}
        paginationServer={!isSearching} // Only use server-side pagination when not searching

        // onSearchIconClick={launchSearch}
        // onHeaderAddBtnClick={toggleCreateModal}
        customHeader={

          <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
            <div className="d-flex gap-2" style={{ flex: '0 0 20%' }}>
              <Button
                color="success"
                onClick={toggleCreateModal}
                className="d-flex align-items-center justify-content-center"
                style={{ padding: '0.4rem 1rem', whiteSpace: 'nowrap' }}
              >
                + {t("Add User")}
              </Button>
            </div>
            <div className="d-flex align-items-center gap-2 w-100" style={{ flex: '0 0 40%' }}>
              <h6 className="text-nowrap mb-0">Search: </h6>
              <div className="flex-grow-1">
                <SearchBar
                  onSearch={searchHandler}
                  disabled={isLoading}
                  placeholder="Search users by name or email..."
                />
              </div>
            </div>

          </div>

        }
        onHeaderDeleteBtnClick={() => {
          alert("Soon");
        }}
        actionColWidth="150px"
        customActionBtns={(row) => (
          <>
            <button
              className="btn btn-sm btn-info"
              onClick={() => togglePermissionsModal(row)}
              title="Edit Permissions"
            >
              <i className="ri-shield-user-line"></i>
            </button>

            <button
              className="btn btn-sm btn-warning"
              onClick={() => toggleEditUserModal(row)}
              title="Edit"
            >
              <i className="ri-edit-fill"></i>
            </button>

            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                showConfirm(
                  () => {
                    deleteUserFun(row.user_id);
                  },
                  () => {
                    console.log("Cancelled");
                  },
                  "Confirmation", // Title of the confirmation dialog
                  `Are you sure you want to delete this account?` // Question displayed in the dialog
                );
              }}
              title="Delete"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      />

      {showEditingModal && (
        <EditUser
          rowData={selectedRow}
          showEditUserModal={showEditingModal}
          toggleEditUserModal={() => setshowEditingModal(!showEditingModal)}
        />
      )}
      {showAddingModal && (
        <AddUser
          showAddingModal={showAddingModal}
          toggleCreateModal={() => setshowAddingModal(!showAddingModal)}
        />
      )}
      {showPermissionsModal && (
        <EditPermissions
          showPermissionsModal={showPermissionsModal}
          togglePermissionsModal={() => setShowPermissionsModal(!showPermissionsModal)}
          rowData={selectedRow}
        />
      )}
    </>
  );
};
